import { useState, useEffect } from 'react';
import { Target, MessageSquare, ChevronDown, ChevronUp, Check, Save } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Skill label mapping
const SKILL_MAP = {
  ball_control: 'Ball Control',
  passing: 'Passing',
  dribbling: 'Dribbling',
  first_touch: 'First Touch',
  finishing: 'Finishing',
  shooting: 'Shooting',
  speed: 'Speed',
  acceleration: 'Acceleration',
  agility: 'Agility',
  coordination: 'Coordination',
  balance: 'Balance',
  explosiveness: 'Explosiveness',
  endurance: 'Endurance',
  positioning: 'Positioning',
  decision_making: 'Decision Making',
  vision: 'Vision',
  game_awareness: 'Game Awareness',
  reaction_time: 'Reaction Time',
};

const getStatusColor = (status) => {
  switch (status) {
    case 'Proficient': return 'bg-green-500/20 text-green-400';
    case 'Developing': return 'bg-yellow-500/20 text-yellow-400';
    case 'Focus': return 'bg-red-500/20 text-red-400';
    default: return 'bg-muted text-muted-foreground';
  }
};

/**
 * Component to display Player Goals in Coach's athlete view
 * Allows coach to see what goals the athlete/parent set and add comments
 */
const CoachAthleteGoals = ({ athleteId, getAuthHeaders }) => {
  const [goals, setGoals] = useState([]);
  const [assessments, setAssessments] = useState({});
  const [loading, setLoading] = useState(true);
  const [expandedGoal, setExpandedGoal] = useState(null);
  const [coachComments, setCoachComments] = useState({});
  const [savingComment, setSavingComment] = useState(null);

  useEffect(() => {
    fetchGoals();
  }, [athleteId]);

  const fetchGoals = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/athletes/${athleteId}/goals`, { headers: getAuthHeaders() });
      setGoals(res.data);
      // Initialize coach comments state
      const comments = {};
      res.data.forEach(g => {
        comments[g.id] = g.coach_comment || '';
      });
      setCoachComments(comments);
    } catch (error) {
      console.error('Failed to fetch player goals:', error);
    }
    setLoading(false);
  };

  const fetchAssessments = async (goalId) => {
    try {
      const res = await axios.get(`${API}/athletes/${athleteId}/goals/${goalId}/assessments`, { headers: getAuthHeaders() });
      setAssessments(prev => ({ ...prev, [goalId]: res.data }));
    } catch (error) {
      console.error('Failed to fetch assessments:', error);
    }
  };

  const handleToggleGoal = async (goalId) => {
    if (expandedGoal === goalId) {
      setExpandedGoal(null);
    } else {
      setExpandedGoal(goalId);
      if (!assessments[goalId]) {
        await fetchAssessments(goalId);
      }
    }
  };

  const handleSaveComment = async (goalId) => {
    setSavingComment(goalId);
    try {
      await axios.post(
        `${API}/athletes/${athleteId}/goals/${goalId}/coach-comment`,
        { comment: coachComments[goalId] },
        { headers: getAuthHeaders() }
      );
      toast.success('Comment saved');
      fetchGoals();
    } catch (error) {
      toast.error('Failed to save comment');
    }
    setSavingComment(null);
  };

  const getLatestAssessment = (goalId, skillKey) => {
    const goalAssessments = assessments[goalId] || [];
    return goalAssessments.find(a => a.skill_key === skillKey);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-32">
        <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (goals.length === 0) {
    return (
      <div className="bg-muted/20 border border-border/40 p-6 rounded-sm text-center">
        <Target className="mx-auto h-10 w-10 text-muted-foreground mb-3" />
        <p className="text-muted-foreground">No player goals set yet</p>
        <p className="text-xs text-muted-foreground mt-1">
          Goals are created by the athlete or parent to express their development priorities
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="coach-athlete-goals">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Target className="text-primary" size={20} />
          <h3 className="font-heading text-lg uppercase">Player Goals</h3>
        </div>
        <span className="text-xs text-muted-foreground">{goals.length} goals</span>
      </div>

      <div className="bg-blue-500/10 border border-blue-500/30 p-3 rounded-sm">
        <p className="text-xs text-blue-400">
          These goals were set by the athlete/parent. Self-assessments shown here reflect their personal perspective and do NOT affect your coach evaluations.
        </p>
      </div>

      <div className="space-y-3">
        {goals.map(goal => (
          <div 
            key={goal.id} 
            className={`bg-card border rounded-sm overflow-hidden ${
              goal.status === 'completed' ? 'border-green-500/30' : 'border-border/40'
            }`}
          >
            {/* Goal Header */}
            <div 
              className="flex items-center justify-between p-3 cursor-pointer hover:bg-muted/20 transition-colors"
              onClick={() => handleToggleGoal(goal.id)}
            >
              <div className="flex items-center gap-3">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                  goal.status === 'completed' ? 'bg-green-500/20' : 'bg-primary/10'
                }`}>
                  {goal.status === 'completed' ? (
                    <Check className="text-green-400" size={16} />
                  ) : (
                    <Target className="text-primary" size={16} />
                  )}
                </div>
                <div>
                  <h4 className="font-medium text-sm">{goal.title}</h4>
                  <p className="text-xs text-muted-foreground">
                    {goal.selected_skills.length} skills • by {goal.created_by}
                    {goal.target_date && ` • Target: ${new Date(goal.target_date).toLocaleDateString()}`}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {goal.status === 'completed' && (
                  <span className="px-2 py-0.5 bg-green-500/20 text-green-400 text-xs rounded">Done</span>
                )}
                {expandedGoal === goal.id ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
              </div>
            </div>

            {/* Expanded Content */}
            {expandedGoal === goal.id && (
              <div className="border-t border-border/40 p-3 space-y-3">
                {/* Why This Goal Matters */}
                {goal.why_this_goal_matters && (
                  <div className="bg-muted/20 p-2 rounded-sm">
                    <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Why This Matters to Them</p>
                    <p className="text-sm">{goal.why_this_goal_matters}</p>
                  </div>
                )}

                {/* Skills & Self-Assessments */}
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wider mb-2">
                    Self-Assessment (Player/Parent Rating)
                  </p>
                  <div className="space-y-2">
                    {goal.selected_skills.map(skillKey => {
                      const latest = getLatestAssessment(goal.id, skillKey);
                      return (
                        <div key={skillKey} className="bg-muted/30 p-3 rounded-sm">
                          <div className="flex items-center justify-between mb-1">
                            <p className="text-sm font-medium">{SKILL_MAP[skillKey] || skillKey}</p>
                            {latest ? (
                              <span className={`text-xs px-2 py-0.5 rounded ${getStatusColor(latest.level)}`}>
                                {latest.level}
                              </span>
                            ) : (
                              <span className="text-xs text-muted-foreground">Not rated</span>
                            )}
                          </div>
                          {latest?.notes && (
                            <div className="mt-2 pl-2 border-l-2 border-primary/30">
                              <p className="text-xs text-muted-foreground italic">"{latest.notes}"</p>
                              <p className="text-xs text-muted-foreground/60 mt-1">
                                {new Date(latest.created_at).toLocaleDateString()}
                              </p>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Coach Comment Section */}
                <div className="pt-2 border-t border-border/40">
                  <p className="text-xs text-muted-foreground uppercase tracking-wider mb-2 flex items-center gap-1">
                    <MessageSquare size={12} />
                    Your Comment (visible to athlete/parent)
                  </p>
                  <div className="flex gap-2">
                    <Textarea
                      value={coachComments[goal.id] || ''}
                      onChange={(e) => setCoachComments(prev => ({ ...prev, [goal.id]: e.target.value }))}
                      placeholder="Add advice or feedback about this goal..."
                      className="bg-transparent text-sm flex-1"
                      rows={2}
                    />
                    <Button
                      size="sm"
                      onClick={() => handleSaveComment(goal.id)}
                      disabled={savingComment === goal.id}
                      className="bg-primary text-black h-auto"
                    >
                      <Save size={14} />
                    </Button>
                  </div>
                  {goal.coach_comment && (
                    <p className="text-xs text-green-400 mt-1">Comment saved</p>
                  )}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default CoachAthleteGoals;
